import { useSelector } from "react-redux";
import { selectHarborCapabilities } from "@/store/harborCapabilitiesSlice";
import { HarborCapability } from "@/constant/harbour";
import { useCurrentUserRoles } from "@/utils/userRole";
import { UserRoles } from "@/constant";

/**
 * Hook to check harbor plan permissions based on stored capabilities
 * @returns Object with permission checking functions
 */
export const usePlanPermissions = () => {
  const capabilities = useSelector(selectHarborCapabilities);
  const roles = useCurrentUserRoles();
  const isMarinaManager = roles.includes(UserRoles.MARINA);

  /**
   * Check if a specific capability is enabled.
   * Non-marina-manager roles bypass capability checks and always return true,
   * since capabilities are only fetched for marina managers.
   */
  const hasCapability = (capability: HarborCapability): boolean => {
    if (!isMarinaManager) {
      return true;
    }
    if (!capabilities || capabilities.length === 0) {
      return false;
    }
    return capabilities.includes(capability);
  };

  /**
   * Check if any of the provided capabilities are enabled
   * @param capabilitiesToCheck - Array of HarborCapability enum values
   * @returns true if at least one capability is enabled, false otherwise
   */
  const hasAnyCapability = (
    capabilitiesToCheck: HarborCapability[],
  ): boolean => {
    if (!isMarinaManager) {
      return true;
    }
    if (!capabilities || capabilities.length === 0) {
      return false;
    }
    return capabilitiesToCheck.some((cap) => capabilities.includes(cap));
  };

  /**
   * Check if all of the provided capabilities are enabled
   * @param capabilitiesToCheck - Array of HarborCapability enum values
   * @returns true if all capabilities are enabled, false otherwise
   */
  const hasAllCapabilities = (
    capabilitiesToCheck: HarborCapability[],
  ): boolean => {
    if (!isMarinaManager) {
      return true;
    }
    if (!capabilities || capabilities.length === 0) {
      return false;
    }
    return capabilitiesToCheck.every((cap) => capabilities.includes(cap));
  };

  /**
   * Get all enabled capabilities
   * @returns Array of enabled capability unique keys
   */
  const getEnabledCapabilities = (): string[] => {
    return capabilities || [];
  };

  /**
   * Check if capabilities are loaded
   * @returns true if capabilities are loaded (even if empty), false if null
   */
  const isCapabilitiesLoaded = (): boolean => {
    return capabilities !== null;
  };

  return {
    hasCapability,
    hasAnyCapability,
    hasAllCapabilities,
    getEnabledCapabilities,
    isCapabilitiesLoaded,
    capabilities,
  };
};
